import React from 'react';
import {
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import { OrigenChip } from '../../../../components/common';
import { type DERCResponse } from '../types/derc';

interface DercInfoPanelProps {
  dercDetails: DERCResponse | null;
  loading?: boolean;
  error?: string | null;
}

const DercInfoPanel: React.FC<DercInfoPanelProps> = ({
  dercDetails,
  loading = false,
  error = null
}) => {
  // Check if origin is "producto b" (id=2) for conditional styling
  const isProductoB = dercDetails?.idOrigenProducto === 2;
  const backgroundColor = isProductoB ? 'rgba(255, 243, 243, 1)' : 'grey.50';
  const borderColor = isProductoB ? 'rgba(255, 205, 205, 1)' : 'grey.200';
  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          p: 3,
          backgroundColor,
          border: '1px solid',
          borderColor,
          borderRadius: 2,
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          gap: 2
        }}>
          <CircularProgress size={20} thickness={4} />
          <Typography variant="body2" color="text.secondary">
            Cargando información...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          p: 3,
          backgroundColor,
          border: '1px solid',
          borderColor,
          borderRadius: 2,
        }}
      >
        <Alert
          severity="error"
          sx={{
            borderRadius: 1.5,
            '& .MuiAlert-message': {
              fontSize: '0.875rem'
            }
          }}
        >
          {error}
        </Alert>
      </Paper>
    );
  }

  if (!dercDetails) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        px: 3,
        py: 1,
        backgroundColor,
        border: '1px solid',
        borderColor,
        borderRadius: 2,
      }}
    >
      {/* Header with Title and Origen Chip */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography
          variant="subtitle2"
          color="primary.main"
          sx={{
            fontWeight: 600,
            fontSize: '0.95rem',
            margin: 0,
          }}
        >
          Información del DERC
        </Typography>
        <OrigenChip
          idOrigenProducto={dercDetails.idOrigenProducto || 1}
          size="small"
        />
      </Box>

      <Grid container spacing={2}>
        <Grid size={{ xs: 6, md: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: '0.7rem'
              }}
            >
              Fecha:
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.8rem' }}>
              {dercDetails.fechaExtraccion
                ? new Date(dercDetails.fechaExtraccion).toLocaleDateString('es-ES')
                : 'N/A'}
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 6, md: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: '0.7rem'
              }}
            >
              Mallas:
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.8rem' }}>
              {dercDetails.numeroMallas || 'N/A'}
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 6, md: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: '0.7rem'
              }}
            >
              Manojos:
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.8rem' }}>
              {dercDetails.numeroManojos || 'N/A'}
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 6, md: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: '0.7rem'
              }}
            >
              Placa:
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.8rem' }}>
              {dercDetails.placaVehiculo || 'N/A'}
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 6, md: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: '0.7rem'
              }}
            >
              Asociación:
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.8rem' }}>
              {dercDetails.asociacionDescripcion || 'N/A'}
            </Typography>
          </Box>
        </Grid>
        {dercDetails.observacion && (
          <Grid size={12}>
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.75rem'
                }}
              >
                Observaciones
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: 'text.primary',
                backgroundColor: 'white',
                p: 1.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}
            >
              {dercDetails.observacion}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default DercInfoPanel;